import { z } from "zod";
import { CART_QTY_MAXVALUE } from "@hsr/utils";
import { CHECKOUT_DELIVERYOPTIONS } from "./checkout";
import type { TFunction } from "i18next";
import { REGEX_USERS_EMAIL } from "./regex";

const BaseSchema = (t: TFunction) =>
  z.object({
    id: z.string(),
    price: z.number(),
    salesUnit: z.string(),
    salesUnits: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
          selected: z.boolean().optional()
        })
      )
      .optional(),
    qty: z
      .number({ message: t(`validation:errors.qtyNotANumber`) })
      .positive({
        message: t(`validation:errors.qtyTooLow`, { x: 1 })
      })
      .min(1, { message: t(`validation:errors.qtyTooLow`, { x: 1 }) })
      .max(CART_QTY_MAXVALUE, {
        message: t(`validation:errors.qtyTooBig`, {
          x: CART_QTY_MAXVALUE
        })
      }),
    qty1: z.number().nullable(), // new 19.08.24
    qty2: z.number(),
    availibility: z.string()
  });

// used in: <CartButtons />, <DialogAddToCart /> & MobileProductCard
export const ValidationSchemaPDP = (t: TFunction) =>
  BaseSchema(t).merge(
    z.object({
      hsrProductNo: z.string(),
      productDetails: z.object({
        id: z.string(),
        hsrProductNo: z.string(),
        price: z
          .object({
            value: z.number().nullable(),
            currency: z.string(),
            companyId: z.string()
          })
          .nullable(),
        stocks: z
          .object({
            quantity: z.number().nullable(),
            secondaryQuantity: z.number().nullable()
          })
          .nullable()
      })
    })
  );

// used in: <CardProductsSection /> & /TableFormWrapper
export const ValidationSchemaWithRows = (t: TFunction) =>
  z.object({
    rows: z
      .array(
        BaseSchema(t).merge(
          z.object({
            hsrProductNo: z.string(),
            productDetails: z.object({
              id: z.string(),
              hsrProductNo: z.string(),
              price: z
                .object({
                  value: z.number().nullable(),
                  currency: z.string(),
                  companyId: z.string()
                })
                .nullable(),
              stocks: z
                .object({
                  quantity: z.number().nullable(),
                  secondaryQuantity: z.number().nullable()
                })
                .nullable()
            })
          })
        )
      )
      .optional()
  });

// used in: <UserManagementForm />
export const ValidationSchemaUser = (t: TFunction) =>
  z
    .object({
      firstName: z.string().min(3, {
        message: `${t("validation:errors.minLength", { x: 3 })}`
      }),
      lastName: z.string().min(3, {
        message: `${t("validation:errors.minLength", { x: 3 })}`
      }),
      username: z.string().optional(),
      id: z.string().optional(),
      email: z.string().min(1, {
        message: `${t("validation:errors.minLength", {
          x: 1
        })}`
      }),
      admin: z.boolean(),
      active: z.boolean()
    })
    // zod email will not accept a + sign. So we need to use a regex to validate the email
    .superRefine((item, ctx) => {
      if (!item.email.match(REGEX_USERS_EMAIL)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation:errors.validEmail"),
          path: ["email"]
        });
      }
      // we don't want to allow plus signs
      if (item.email.includes("+")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation:errors.validEmailPlus"),
          path: ["email"]
        });
      }
    });

// used in: /OrdersPage/RequestForm
export const ValidationSchemaRequest = (t: TFunction) =>
  z.object({
    requestItems: z
      .array(
        z
          .object({
            orderItemID: z.string().optional(),
            orderItemName: z.string(),
            reasonDropdown: z.string(),
            checked: z.boolean()
          })
          // Check on selected checkboxes if dropdown has any value
          .superRefine((item, ctx) => {
            if (item.checked && item.reasonDropdown === "-1") {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("validation:errors.returnRequest.required"),
                path: ["reasonDropdown"]
              });
            }
          })
      )
      // Check if any checkbx is selected
      .superRefine((items, ctx) => {
        if (items.every((item) => !item.checked)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("validation:errors.returnRequest.itemRequired")
          });
          return z.NEVER;
        }
        return z.NEVER;
      })
  });

export const ValidationSchemaCheckout = (t: TFunction) =>
  z
    .object({
      id: z.string().optional(),
      shippingAddressID: z.string(),
      comments: z.string().nullable().default(null),
      minSurcharge: z.number().nullable().default(null),
      deliveryAddress: z.enum(CHECKOUT_DELIVERYOPTIONS, {
        required_error: `${t("checkout:errors.required")}`
      }),
      companyName: z.string(),
      street1: z.string(),
      street2: z.string().optional(),
      zip: z.string(),
      city: z.string(),
      phone: z.string().optional(),
      country: z.string(),
      saveToAddressBook: z.boolean(),
      terms: z.boolean(),
      // added manually for shipping methods radio buttons
      selectedShipMethodId: z.string()
    })
    .superRefine((schema, ctx) => {
      const { companyName, street1, zip, city, terms } = schema;

      // terms and conditions must be accepted ONLY if deliveryAddress is NOT newDeliveryAddress
      if (schema.deliveryAddress !== "newDeliveryAddress" && !terms) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${t("validation:errors.terms")}`,
          path: ["terms"]
        });
      }

      // check if companyName is empty
      if (
        schema.deliveryAddress === "newDeliveryAddress" &&
        (!companyName || companyName.length <= 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: "string",
          inclusive: true,
          message: `${t("validation:errors.required")}`,
          path: ["companyName"]
        });
      }

      // check if street1 (street and house number) is empty
      if (
        schema.deliveryAddress === "newDeliveryAddress" &&
        (!street1 || street1.length <= 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: "string",
          inclusive: true,
          message: `${t("validation:errors.required")}`,
          path: ["street1"]
        });
      }
      // check if postCode is empty
      if (
        schema.deliveryAddress === "newDeliveryAddress" &&
        (!zip || zip.length < 2)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 2,
          type: "string",
          inclusive: true,
          message: `${t("validation:errors.minLength", { x: 2 })}`,
          path: ["zip"]
        });
      }
      // check if city is empty
      if (
        schema.deliveryAddress === "newDeliveryAddress" &&
        (!city || city.length <= 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 2,
          type: "string",
          inclusive: true,
          message: `${t("validation:errors.minLength", { x: 2 })}`,
          path: ["city"]
        });
      }
    });

export const ValidationSchemaRenameCart = () =>
  z.object({
    name: z.string().min(10).max(255)
  });
